import { useNavigate } from 'react-router-dom';
import { scrollToSection } from '@/utils/scrollToSection';

const serviceCards = [
  {
    icon: 'ri-box-3-line',
    title: 'Smart Fulfillment',
    desc: 'Barcode-verified pick & pack with 99.7% accuracy, custom packaging, and QC.',
    badge: 'Pick & Pack',
    anchor: 'smart-fulfillment',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Flexible Storage',
    desc: 'Pay only for what you use. Scale up in peak seasons, down in slow periods.',
    badge: 'Pay-as-you-use',
    anchor: 'flexible-storage',
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Inventory Management',
    desc: 'Real-time SKU tracking, automated reorder alerts, and FIFO rules.',
    badge: 'Real-Time',
    anchor: 'inventory-management',
  },
  {
    icon: 'ri-truck-line',
    title: 'Last-Mile Delivery Coordination',
    desc: 'Same-day in Lagos, next-day nationwide. Live GPS and ePOD.',
    badge: 'Nationwide',
    anchor: 'last-mile-delivery',
  },
  {
    icon: 'ri-shopping-bag-3-line',
    title: 'Ecommerce Fulfillment',
    desc: 'Native integrations with Shopify, WooCommerce, Jumia, and Konga.',
    badge: 'Auto-Fulfillment',
    anchor: 'ecommerce-fulfillment',
  },
];

const consultingFeatures = [
  { title: 'Supply Chain Audit', sub: 'End-to-end diagnostic' },
  { title: 'Network Design', sub: 'Optimal routing' },
  { title: 'Tech Stack', sub: 'Right tools for your stage' },
  { title: 'Cost Reduction', sub: 'SLA & ops improvements' },
];

export default function ServicesSection() {
  const navigate = useNavigate();

  const goToService = (anchor: string) => {
    navigate('/services');
    scrollToSection(anchor);
  };

  return (
    <section className="py-24 px-6" style={{ background: '#0f0f18' }}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <p className="eyebrow">What We Do</p>
          <h2 className="section-heading max-w-2xl">End-to-End Logistics Infrastructure</h2>
          <p className="section-lead max-w-xl">
            From receiving inventory to delivering to your customer. THL handles the entire fulfillment lifecycle.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {serviceCards.map((card) => (
            <div
              key={card.title}
              className="bg-thl-dark3 border border-thl-border rounded-xl p-6 hover:border-thl-red/40 transition-all duration-300 group cursor-pointer"
              onClick={() => goToService(card.anchor)}
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-thl-red/10 border border-thl-red/20 mb-5 group-hover:bg-thl-red/20 transition-colors duration-300">
                <i className={`${card.icon} text-thl-red text-3xl`} />
              </div>
              <h3 className="text-white font-bold text-lg mb-2 group-hover:text-thl-red transition-colors">{card.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed mb-4">{card.desc}</p>
              <span className="text-xs font-semibold text-thl-red border border-thl-red/30 rounded-full px-3 py-1">
                {card.badge}
              </span>
            </div>
          ))}

          {/* Wide card — Supply Chain Consulting */}
          <div
            className="sm:col-span-2 lg:col-span-3 bg-thl-dark3 border border-thl-border rounded-xl p-6 hover:border-thl-red/40 transition-all duration-300 cursor-pointer"
            onClick={() => goToService('consulting')}
          >
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-1/3">
                <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-thl-red/10 border border-thl-red/20 mb-5">
                  <i className="ri-route-line text-thl-red text-3xl" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Supply Chain Consulting</h3>
                <p className="text-white/50 text-sm leading-relaxed mb-4">
                  Strategic advisory to optimize your supply chain and reduce costs.
                </p>
                <span className="text-xs font-semibold text-thl-red border border-thl-red/30 rounded-full px-3 py-1">
                  Expert Advisory
                </span>
              </div>
              <div className="lg:w-2/3 grid grid-cols-2 gap-3">
                {consultingFeatures.map(feat => (
                  <div key={feat.title} className="bg-white/5 rounded-lg p-4">
                    <div className="text-white font-semibold text-sm mb-1">{feat.title}</div>
                    <div className="text-white/40 text-xs">{feat.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
